import { useQuery } from '@apollo/client';
import { CURRENT_USER_OPERABLE_WAREHOUSE_IDS } from '../graphql/queries/currentUserOperableWarehouseIds';

type Options = {
    skip?: boolean;
    fetchPolicy?: 'cache-first' | 'cache-only' | 'network-only' | 'no-cache' | 'standby';
};

type CurrentUserOperableWarehouseIdsResponse = {
    /** GraphQL Long/ID 常序列化为字符串，需归一化 */
    currentUserOperableWarehouseIds: (number | string)[];
};

export function normalizeOperableWarehouseIds(raw: readonly (number | string)[] | undefined | null): number[] {
    if (!raw?.length) return [];
    return raw.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
}

/** 含 0 表示全量仓权限（兼容接口返回字符串 "0"） */
export function hasFullWarehouseAccessFromIds(warehouseIds: readonly (number | string)[]): boolean {
    return warehouseIds.some((id) => Number(id) === 0);
}

/**
 * 获取当前登录用户有权限操作的仓库 ID 列表。
 * - 含 0：全量仓（hasFullWarehouseAccess）
 * - 具体 ID：仅限这些仓/Hub
 *
 * 登录后写入 Redis 请用 useFetchWarehouseIds（network-only，同源 query）。
 */
export function useCurrentUserOperableWarehouseIds(options?: Options) {
    const { skip = false, fetchPolicy = 'cache-first' } = options || {};

    const { data, loading, error, refetch } = useQuery<CurrentUserOperableWarehouseIdsResponse>(
        CURRENT_USER_OPERABLE_WAREHOUSE_IDS,
        {
            skip,
            fetchPolicy,
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: false,
        }
    );

    const warehouseIds = normalizeOperableWarehouseIds(data?.currentUserOperableWarehouseIds);

    return {
        warehouseIds,
        hasFullWarehouseAccess: hasFullWarehouseAccessFromIds(warehouseIds),
        loading,
        error,
        refetch,
    };
}
